import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    Sparkles, RefreshCw, Layers, Cpu, Database, Trash2, FileText,
    ChevronDown, ChevronRight, AlertTriangle, GitBranch, X,
    CheckCircle2, ShieldCheck, Link2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
    draft: "bg-white/5 border-white/20 text-white/60",
    needs_review: "bg-amber-400/10 border-amber-400/30 text-amber-400",
    approved: "bg-emerald-400/10 border-emerald-400/30 text-emerald-400",
    superseded: "bg-white/[0.03] border-white/10 text-white/30",
};

const SOURCE_TONE = {
    protocol: "border-violet-400/40 text-violet-300 bg-violet-400/5",
    edc_guidelines: "border-sky-400/40 text-sky-300 bg-sky-400/5",
};

export default function QaFormGenerationPage() {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStudy, setFilterStudy] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [onlyCurrent, setOnlyCurrent] = useState(false);
    const [genOpen, setGenOpen] = useState(false);
    const [openPkgId, setOpenPkgId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterStudy) params.set("study_id", filterStudy);
            if (filterStatus) params.set("status", filterStatus);
            if (onlyCurrent) params.set("only_current", "true");
            const { data } = await api.get(`/v1/forms/packages?${params.toString()}`);
            setPackages(data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setLoading(false); }
    }, [filterStudy, filterStatus, onlyCurrent]);

    useEffect(() => { load(); }, [load]);

    const studies = useMemo(() => {
        const set = new Set();
        for (const p of packages) if (p.studyID) set.add(p.studyID);
        return Array.from(set).sort();
    }, [packages]);

    return (
        <div className="px-8 py-8" data-testid="qa-form-generation-page">
            <div className="flex items-start justify-between mb-7">
                <div>
                    <h1 className="text-2xl font-display tracking-tight mb-1">Form Generation</h1>
                    <p className="text-sm text-white/50 max-w-3xl">
                        QA testing point for the <strong>Form Generation</strong> agent + flow.
                        Generates the <strong>union of all protocol-required and EDC-required datapoints</strong> as a versioned, traceable form package.
                        Stored in the datalake — never regenerated by Get-Forms.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={load}
                        data-testid="qa-formgen-refresh-btn"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-xs text-white/70 hover:text-white hover:bg-white/5"
                    >
                        <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />Refresh
                    </button>
                    <button
                        type="button"
                        onClick={() => setGenOpen(true)}
                        data-testid="qa-formgen-generate-btn"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#7C3AED] hover:bg-[#6D28D9] text-sm font-medium"
                    >
                        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
                        Generate form package
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                <Pill icon={Cpu} title="Agent" value="form-generation-package" sub="active · v1" tone="violet" />
                <Pill icon={Layers} title="Flow" value="forms-generate-package" sub="published · prod" tone="sky" />
                <Pill icon={Database} title="Datalake kind" value="generated_form_package" sub="versioned, never overwritten" tone="emerald" />
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-5 p-3 rounded-md border border-white/10 bg-white/[0.02]">
                <select
                    value={filterStudy}
                    onChange={(e) => setFilterStudy(e.target.value)}
                    data-testid="qa-formgen-filter-study"
                    className="bg-[#0a0a0a] border border-white/15 rounded px-2 py-1 text-xs text-white/80"
                >
                    <option value="">All studies</option>
                    {studies.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    data-testid="qa-formgen-filter-status"
                    className="bg-[#0a0a0a] border border-white/15 rounded px-2 py-1 text-xs text-white/80"
                >
                    <option value="">Any status</option>
                    <option value="draft">draft</option>
                    <option value="needs_review">needs_review</option>
                    <option value="approved">approved</option>
                    <option value="superseded">superseded</option>
                </select>
                <label className="flex items-center gap-1.5 text-xs text-white/60 ml-2">
                    <input type="checkbox" checked={onlyCurrent}
                        onChange={(e) => setOnlyCurrent(e.target.checked)}
                        data-testid="qa-formgen-only-current" />
                    Only current versions
                </label>
                <span className="ml-auto text-xs text-white/40">{packages.length} package{packages.length === 1 ? "" : "s"}</span>
            </div>

            {loading ? (
                <div className="text-white/40 text-sm">Loading…</div>
            ) : packages.length === 0 ? (
                <div className="p-10 rounded-md border border-dashed border-white/15 text-center" data-testid="qa-formgen-empty">
                    <FileText className="w-8 h-8 mx-auto text-white/20 mb-3" strokeWidth={1.5} />
                    <div className="text-sm text-white/60 mb-1">No form packages generated yet</div>
                    <div className="text-xs text-white/40 mb-4">
                        Pick a Protocol × EDC pair below to generate the first package.
                    </div>
                    <button
                        type="button"
                        onClick={() => setGenOpen(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#7C3AED] hover:bg-[#6D28D9] text-sm font-medium"
                    >
                        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />Generate
                    </button>
                </div>
            ) : (
                <div className="space-y-2">
                    {packages.map((pkg) => (
                        <PackageRow
                            key={pkg.id}
                            pkg={pkg}
                            opened={openPkgId === pkg.id}
                            onToggle={() => setOpenPkgId(openPkgId === pkg.id ? null : pkg.id)}
                            onDeleted={load}
                        />
                    ))}
                </div>
            )}

            {genOpen && (
                <GenerateModal
                    onClose={() => setGenOpen(false)}
                    onDone={() => { setGenOpen(false); load(); }}
                />
            )}
        </div>
    );
}

function Pill({ icon: Icon, title, value, sub, tone }) {
    const toneClass = {
        violet: "border-violet-400/20 bg-violet-400/[0.04] text-violet-300",
        sky: "border-sky-400/20 bg-sky-400/[0.04] text-sky-300",
        emerald: "border-emerald-400/20 bg-emerald-400/[0.04] text-emerald-300",
    }[tone] || "border-white/10 bg-white/[0.02]";
    return (
        <div className={`flex items-start gap-3 p-3 rounded-md border ${toneClass}`}>
            <Icon className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.75} />
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide opacity-70">{title}</div>
                <div className="text-sm font-mono truncate">{value}</div>
                <div className="text-xs opacity-50">{sub}</div>
            </div>
        </div>
    );
}

function PackageRow({ pkg, opened, onToggle, onDeleted }) {
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    useEffect(() => {
        if (opened && !detail) {
            setLoadingDetail(true);
            api.get(`/v1/forms/packages/${pkg.id}`)
                .then(({ data }) => setDetail(data))
                .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)))
                .finally(() => setLoadingDetail(false));
        }
    }, [opened, detail, pkg.id]);

    const onDelete = async () => {
        if (!confirm(`Soft-archive package ${pkg.generatedFormPackageVersion}?\n\nThe row is preserved but marked superseded.`)) return;
        try {
            await api.delete(`/v1/forms/packages/${pkg.id}`);
            toast.success("Archived");
            onDeleted();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    return (
        <div className="rounded-md border border-white/10 bg-white/[0.02]" data-testid={`formgen-package-${pkg.id}`}>
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-white/[0.03]"
            >
                {opened ? <ChevronDown className="w-4 h-4 text-white/40" strokeWidth={1.5} /> : <ChevronRight className="w-4 h-4 text-white/40" strokeWidth={1.5} />}
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-white">{pkg.studyID}</span>
                        <span className="text-white/30">·</span>
                        <span className="font-mono text-xs text-white/60">protocol {pkg.protocolVersion}</span>
                        <span className="text-white/30">×</span>
                        <span className="font-mono text-xs text-white/60">EDC {pkg.edcGuidelinesVersion}</span>
                        <span className="text-white/30">→</span>
                        <span className="font-mono text-xs text-white">pkg {pkg.generatedFormPackageVersion}</span>
                        {pkg.is_current_version && (
                            <span className="px-1.5 py-0.5 rounded border border-emerald-400/30 text-emerald-400 text-[10px]">CURRENT</span>
                        )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-white/50">
                        <span>{pkg.visit_count} visits · {pkg.form_count} forms · {pkg.field_count} fields</span>
                        <span className="font-mono text-[10px] text-white/40">agent v{pkg.agent_version_no} · flow v{pkg.flow_version_no}</span>
                        <span className="font-mono text-[10px] text-white/40">${(pkg.llm_cost_usd || 0).toFixed(4)}</span>
                    </div>
                </div>
                <span className={`px-1.5 py-0.5 rounded border text-[10px] ${STATUS_BADGE[pkg.generationStatus] || ""}`}>
                    {pkg.generationStatus}
                </span>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    title="Archive"
                    data-testid={`formgen-package-delete-${pkg.id}`}
                    className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-red-400"
                >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
            </button>

            {opened && (
                <div className="border-t border-white/10 p-4">
                    {loadingDetail || !detail ? (
                        <div className="text-white/40 text-sm">Loading package…</div>
                    ) : (
                        <PackageDetail pkg={detail} />
                    )}
                </div>
            )}
        </div>
    );
}

function PackageDetail({ pkg }) {
    const conflicts = pkg.conflicts || [];
    const reviewItems = pkg.humanReviewItems || [];
    const globalWarnings = pkg.globalWarnings || [];

    return (
        <div className="space-y-4" data-testid={`formgen-package-detail-${pkg.id}`}>
            {/* Warnings/conflicts/review panels */}
            {(conflicts.length > 0 || reviewItems.length > 0 || globalWarnings.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {conflicts.length > 0 && (
                        <Panel title="Conflicts" tone="red" count={conflicts.length} data-testid="formgen-conflicts">
                            {conflicts.map((c, i) => (
                                <div key={i} className="text-[11px] py-1 border-b border-white/5 last:border-b-0">
                                    <div className="font-mono text-red-300 truncate">{c.field}</div>
                                    <div className="text-white/60">
                                        protocol: <span className="font-mono">{String(c.protocolValue ?? "—")}</span> /{" "}
                                        EDC: <span className="font-mono">{String(c.edcValue ?? "—")}</span>
                                    </div>
                                    {c.resolution && <div className="text-white/40">→ {c.resolution}</div>}
                                </div>
                            ))}
                        </Panel>
                    )}
                    {reviewItems.length > 0 && (
                        <Panel title="Human review items" tone="amber" count={reviewItems.length}>
                            {reviewItems.map((r, i) => (
                                <div key={i} className="text-[11px] py-1 border-b border-white/5 last:border-b-0">
                                    <span className="font-mono text-amber-300">{r.category || "general"}</span>
                                    <div className="text-white/70">{r.details}</div>
                                    {r.suggestedAction && <div className="text-white/40">→ {r.suggestedAction}</div>}
                                </div>
                            ))}
                        </Panel>
                    )}
                    {globalWarnings.length > 0 && (
                        <Panel title="Global warnings" tone="white" count={globalWarnings.length}>
                            {globalWarnings.map((w, i) => (
                                <div key={i} className="text-[11px] py-1 text-white/60">· {typeof w === "string" ? w : JSON.stringify(w)}</div>
                            ))}
                        </Panel>
                    )}
                </div>
            )}

            {/* Visit tree */}
            <div className="space-y-2">
                {(pkg.visits || []).map((v, i) => (
                    <VisitBlock key={`${v.visitID || i}-${v.visitName || i}`} visit={v} />
                ))}
            </div>
        </div>
    );
}

function Panel({ title, tone, count, children }) {
    const toneCls = {
        red: "border-red-400/30 bg-red-400/5",
        amber: "border-amber-400/30 bg-amber-400/5",
        white: "border-white/15 bg-white/[0.02]",
    }[tone];
    return (
        <div className={`rounded border ${toneCls} p-2 max-h-64 overflow-y-auto`}>
            <div className="flex items-center gap-1.5 mb-1 text-xs">
                <AlertTriangle className="w-3 h-3" strokeWidth={1.75} />
                <span className="font-medium">{title}</span>
                <span className="text-white/40">{count}</span>
            </div>
            <div>{children}</div>
        </div>
    );
}

function VisitBlock({ visit }) {
    const [open, setOpen] = useState(false);
    const forms = visit.forms || [];
    return (
        <div className="rounded border border-white/10 bg-white/[0.02]">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.03]"
            >
                {open ? <ChevronDown className="w-3.5 h-3.5 text-white/40" strokeWidth={1.5} /> : <ChevronRight className="w-3.5 h-3.5 text-white/40" strokeWidth={1.5} />}
                <span className="font-mono text-xs text-white/50">#{visit.visitOrder || "?"}</span>
                <span className="text-sm text-white">{visit.visitName || visit.visitID}</span>
                {visit.protocolVisitWindow && (
                    <span className="text-[10px] font-mono text-sky-300">
                        {visit.protocolVisitWindow.earliestDay ?? "?"} / {visit.protocolVisitWindow.targetDay ?? "?"} / {visit.protocolVisitWindow.latestDay ?? "?"} d
                    </span>
                )}
                <span className="text-xs text-white/40 ml-auto">{forms.length} form{forms.length === 1 ? "" : "s"}</span>
            </button>
            {open && (
                <div className="border-t border-white/5 px-3 py-2 space-y-2">
                    {forms.length === 0 && (
                        <div className="text-xs text-white/30">No forms mapped to this visit.</div>
                    )}
                    {forms.map((f, i) => (
                        <FormBlock key={`${f.formID || i}-${i}`} form={f} />
                    ))}
                </div>
            )}
        </div>
    );
}

function FormBlock({ form }) {
    const [open, setOpen] = useState(false);
    const fields = form.fields || [];
    const sources = form.source || [];
    return (
        <div className="rounded border border-white/5 bg-white/[0.01]">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/[0.03]"
            >
                {open ? <ChevronDown className="w-3 h-3 text-white/30" strokeWidth={1.5} /> : <ChevronRight className="w-3 h-3 text-white/30" strokeWidth={1.5} />}
                <span className="text-sm text-white truncate">{form.formName}</span>
                {form.formCategory && <span className="text-[10px] font-mono text-violet-300">{form.formCategory}</span>}
                {sources.map((s, i) => (
                    <span key={i} className={`px-1 py-0.5 rounded border text-[9px] ${SOURCE_TONE[s] || "border-white/20 text-white/60"}`}>{s}</span>
                ))}
                {form.humanReviewRequired && (
                    <span className="text-[10px] text-amber-400 flex items-center gap-0.5">
                        <AlertTriangle className="w-2.5 h-2.5" strokeWidth={1.75} />review
                    </span>
                )}
                <span className="ml-auto text-[10px] text-white/40">{fields.length} field{fields.length === 1 ? "" : "s"}</span>
            </button>
            {open && (
                <div className="border-t border-white/5 px-3 py-1.5 space-y-1">
                    {fields.map((f, i) => (
                        <FieldRow key={`${f.fieldID || f.fieldName || i}-${i}`} field={f} />
                    ))}
                </div>
            )}
        </div>
    );
}

function FieldRow({ field }) {
    const [showTrace, setShowTrace] = useState(false);
    const trace = field.sourceTraceability || [];
    return (
        <div className="text-[11px] py-0.5">
            <div className="grid grid-cols-12 gap-2">
                <span className="col-span-4 text-white/80 truncate">{field.fieldLabel || field.fieldName}</span>
                <span className="col-span-2 font-mono text-white/40">{field.fieldType}</span>
                <span className="col-span-1 text-red-400">{field.required ? "req" : ""}</span>
                <span className="col-span-1 text-white/40">{field.units || ""}</span>
                <span className="col-span-2 flex items-center gap-1">
                    {trace.map((t, i) => (
                        <span key={i} className={`px-1 py-0.5 rounded border text-[9px] ${SOURCE_TONE[t.sourceType] || ""}`}>
                            {t.sourceType === "protocol" ? "P" : "E"}
                        </span>
                    ))}
                    {field.humanReviewRequired && <AlertTriangle className="w-2.5 h-2.5 text-amber-400" strokeWidth={1.75} />}
                </span>
                <span className="col-span-2 text-right">
                    <button
                        type="button"
                        onClick={() => setShowTrace(!showTrace)}
                        className="text-[10px] text-white/40 hover:text-white"
                    >
                        <Link2 className="w-2.5 h-2.5 inline mr-0.5" strokeWidth={1.5} />
                        {trace.length} source{trace.length === 1 ? "" : "s"}
                    </button>
                </span>
            </div>
            {showTrace && trace.map((t, i) => (
                <div key={i} className="ml-4 mt-1 text-[10px] text-white/50 border-l border-white/10 pl-2">
                    <span className={SOURCE_TONE[t.sourceType] ? "text-white/70" : ""}>
                        <strong>{t.sourceType}</strong> ({t.sourceVersion || "?"})
                    </span>
                    {t.section && <span className="ml-2">§{t.section}</span>}
                    {t.page && <span className="ml-2">p.{t.page}</span>}
                    {t.quotedRequirement && (
                        <div className="italic text-white/60 mt-0.5">"{t.quotedRequirement}"</div>
                    )}
                </div>
            ))}
        </div>
    );
}

function GenerateModal({ onClose, onDone }) {
    const [protocols, setProtocols] = useState([]);
    const [frameworks, setFrameworks] = useState([]);
    const [clientId, setClientId] = useState("");
    const [studyId, setStudyId] = useState("");
    const [protocolVersion, setProtocolVersion] = useState("");
    const [edcVersion, setEdcVersion] = useState("");
    const [notes, setNotes] = useState("");
    const [scope, setScope] = useState("full");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [p, f] = await Promise.all([
                    api.get("/admin/protocols?limit=200"),
                    api.get("/admin/edc-frameworks/versions/by-study"),
                ]);
                setProtocols(p.data.items || []);
                setFrameworks(f.data.items || []);
            } catch (e) {
                toast.error(formatApiErrorDetail(e?.response?.data?.detail));
            } finally { setLoading(false); }
        })();
    }, []);

    // Auto-populate study + client when a protocol is selected
    const matchingProtocols = useMemo(() => {
        if (!studyId) return protocols;
        return protocols.filter((p) => p.protocol_number === studyId || p.id === studyId);
    }, [protocols, studyId]);

    const matchingFrameworks = useMemo(() => {
        if (!clientId) return frameworks;
        return frameworks.filter((g) => g.company_id === clientId)
            .filter((g) => !studyId || g.identity === studyId || g.protocol_number === studyId);
    }, [frameworks, clientId, studyId]);

    const studyOptions = useMemo(() => {
        const set = new Set();
        for (const p of protocols) if (p.protocol_number) set.add(p.protocol_number);
        for (const g of frameworks) {
            if (g.study_id) set.add(g.study_id);
            else if (g.protocol_number) set.add(g.protocol_number);
        }
        return Array.from(set).sort();
    }, [protocols, frameworks]);

    const submit = async (e) => {
        e?.preventDefault();
        if (!clientId.trim() || !studyId.trim() || !protocolVersion.trim() || !edcVersion.trim()) {
            toast.error("All four identity fields are required");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/v1/forms/generate-package", {
                client_id: clientId.trim(),
                study_id: studyId.trim(),
                protocol_version: protocolVersion.trim(),
                edc_guidelines_version: edcVersion.trim(),
                generation_notes: notes.trim() || undefined,
                target_scope: scope || undefined,
            }, { timeout: 300000 });
            toast.success(
                `Generated ${data.generatedFormPackageVersion} · ${data.visit_count} visits · ${data.form_count} forms · ${data.field_count} fields` +
                (data.conflicts?.length ? ` · ⚠ ${data.conflicts.length} conflicts` : "") +
                (data.humanReviewItems?.length ? ` · ${data.humanReviewItems.length} review items` : "")
            );
            onDone();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail || e?.message));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="qa-formgen-modal">
            <div className="w-full max-w-lg rounded-lg border border-white/15 bg-[#0a0a0a]">
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-violet-400" strokeWidth={1.75} />
                        <h2 className="text-sm font-medium">Generate form package</h2>
                    </div>
                    <button onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="qa-formgen-modal-close">
                        <X className="w-4 h-4 text-white/60" strokeWidth={1.5} />
                    </button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-3 text-xs">
                    <p className="text-white/50 -mt-1">
                        Resolves the protocol (by client/study/version) and the EDC framework of an <Link to="/qa/ingest-files?type=edc_guidelines" className="underline">ingested EDC Guidelines file</Link> (by client/study/edc-version),
                        runs the published <span className="font-mono">forms-generate-package</span> agent and persists
                        a versioned package.
                    </p>
                    {loading ? (
                        <div className="text-white/40">Loading available studies…</div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Client ID *">
                                    <input
                                        type="text" value={clientId}
                                        onChange={(e) => setClientId(e.target.value)}
                                        placeholder="acme-pharma"
                                        data-testid="qa-formgen-client-id"
                                        className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2 font-mono"
                                    />
                                </Field>
                                <Field label="Study / Protocol number *">
                                    <input
                                        type="text" value={studyId} list="study-options"
                                        onChange={(e) => setStudyId(e.target.value)}
                                        placeholder="ACME-2026-001"
                                        data-testid="qa-formgen-study-id"
                                        className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2 font-mono"
                                    />
                                    <datalist id="study-options">
                                        {studyOptions.map((s) => <option key={s} value={s} />)}
                                    </datalist>
                                </Field>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Protocol version *">
                                    <select
                                        value={protocolVersion}
                                        onChange={(e) => setProtocolVersion(e.target.value)}
                                        data-testid="qa-formgen-protocol-version"
                                        className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2 font-mono"
                                    >
                                        <option value="">Pick…</option>
                                        {matchingProtocols.map((p) => (
                                            <option key={p.id} value={p.version}>{p.version}{p.protocol_number ? ` · ${p.protocol_number}` : ""}</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="EDC Guidelines version * (from Ingest Files)">
                                    <select
                                        value={edcVersion}
                                        onChange={(e) => setEdcVersion(e.target.value)}
                                        data-testid="qa-formgen-edc-version"
                                        className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2 font-mono"
                                    >
                                        <option value="">Pick…</option>
                                        {matchingFrameworks.flatMap((g) => g.versions || []).map((v) => (
                                            <option key={v.id} value={v.edc_guidelines_version}>
                                                {v.edc_guidelines_version}
                                                {v.is_current_version ? " · current" : ""}
                                                {` · ${v.field_count} fields`}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                            </div>
                            <Field label="Generation notes (optional)">
                                <textarea
                                    value={notes} onChange={(e) => setNotes(e.target.value)}
                                    rows={2}
                                    data-testid="qa-formgen-notes"
                                    className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2"
                                />
                            </Field>
                            <Field label="Target scope">
                                <select
                                    value={scope} onChange={(e) => setScope(e.target.value)}
                                    data-testid="qa-formgen-scope"
                                    className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2 font-mono"
                                >
                                    <option value="full">full (all visits, all forms)</option>
                                    <option value="visits:Screening,Baseline">visits: Screening, Baseline</option>
                                    <option value="categories:eligibility,safety">categories: eligibility, safety</option>
                                </select>
                            </Field>
                            <div className="flex items-start gap-2 p-2 rounded border border-violet-400/15 bg-violet-400/[0.03] text-violet-300/80">
                                <Sparkles className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={1.75} />
                                <span>
                                    Dispatches through <Link to="/flows" className="underline">flow forms-generate-package</Link>. Re-running with the same identity quad auto-bumps to <span className="font-mono">v{"{N+1}"}</span>; the previous version stays accessible.
                                </span>
                            </div>
                        </>
                    )}
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-3 py-1.5 rounded text-white/60 hover:text-white">Cancel</button>
                        <button
                            type="submit"
                            disabled={submitting || loading || !clientId.trim() || !studyId.trim() || !protocolVersion.trim() || !edcVersion.trim()}
                            data-testid="qa-formgen-submit"
                            className="flex items-center gap-1.5 px-4 py-2 rounded bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-50"
                        >
                            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
                            {submitting ? "Generating…" : "Generate"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-white/40 mb-1">{label}</div>
            {children}
        </label>
    );
}
